import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import {
  Users, Wallet, TrendingDown, ArrowRight, Play, Megaphone,
  TrendingUp, Users2, LineChart, BadgePercent, CheckCircle,
  MousePointerClick, Sliders, CheckSquare, Award, Clock,
  AlertCircle, ShieldCheck, HelpCircle, Star, ThumbsUp,
  MapPin, Mail, Phone, MessageSquare, ChevronRight, User
} from 'lucide-react';
import InternationalPhoneInput from '../../components/Common/InternationalPhoneInput/InternationalPhoneInput';
import './GrowPage.css';
// Defined outside component so the useEffect closure always references
// the same stable array — no stale-closure risk on re-renders.
const GROW_SIDEBAR_ITEMS = [
  { label: 'Advertise Your Products', id: 'advertise-products' },
  { label: 'How Campaign Works', id: 'how-campaign-works' },
  { label: 'Product Recommendation', id: 'product-recommendation' },
  { label: 'Recommendations', id: 'recommendations-ways' },
  { label: 'Increase Sales', id: 'increase-sales' },
  { label: 'Price Recommendation', id: 'price-recommendation' },
  { label: 'Quality Dashboard', id: 'quality-dashboard' },
  { label: 'Next Day Dispatch Program', id: 'ndd-program' },
  { label: 'Benefits of Registering', id: 'ndd-benefits' },
  { label: 'Steps to Register', id: 'ndd-steps' }
];
export default function GrowPage() {
  const [activeSection, setActiveSection] = useState(null);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    countryCode: '+91',
    email: '',
    city: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const sectionsRef = useRef({});
  const [sidebarState, setSidebarState] = useState('top'); // 'top' | 'fixed' | 'docked'
  const mainLayoutRef = useRef(null);
  const sidebarNavRef = useRef(null);
  const sidebarItems = GROW_SIDEBAR_ITEMS;
  const activeSectionRef = useRef(activeSection);
  activeSectionRef.current = activeSection;
  // Position-based ScrollSpy: active item = last section whose top has
  // crossed the trigger line (just below the sticky navbar/header).
  useLayoutEffect(() => {
    let ticking = false;
    // Recomputed on every call (not a fixed constant) so it scales with
    // viewport height and stays correct across resizes: a section is
    // "active" once its heading has entered the upper third of the
    // screen — i.e. right as you scroll INTO it — instead of only once
    // it has scrolled almost all the way up to the header (which felt
    // like it was switching "at the end" of the previous section).
    const getTriggerLine = () => Math.max(120, window.innerHeight * 0.15);
    const computeActiveSection = () => {
      const TRIGGER_LINE = getTriggerLine();
      let current = null;
      // Near the very bottom of the page (e.g. short last sections that
      // never get a chance to cross TRIGGER_LINE on their own), just
      // force-select the last item.
      const atBottom =
        window.innerHeight + window.pageYOffset >=
        document.documentElement.scrollHeight - 4;
      if (atBottom) {
        current = GROW_SIDEBAR_ITEMS[GROW_SIDEBAR_ITEMS.length - 1]?.id ?? current;
      } else {
        let lastPassed = null;
        for (const item of GROW_SIDEBAR_ITEMS) {
          const el = document.getElementById(item.id);
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          if (rect.top <= TRIGGER_LINE && rect.bottom > TRIGGER_LINE) {
            current = item.id;
            break;
          }
          if (rect.top <= TRIGGER_LINE) {
            lastPassed = item.id;
          }
        }
        // No section straddles the trigger line exactly (e.g. between two
        // short sections) — fall back to the last one whose top has
        // already passed, so the indicator never goes blank mid-scroll.
        if (!current) {
          current = lastPassed;
        }
      }
      if (current !== activeSectionRef.current) {
        setActiveSection(current);
      }
      ticking = false;
    };
    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(computeActiveSection);
      }
    };
    // Run once on mount to set correct initial state
    computeActiveSection();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);
  const handleSidebarClick = (id) => {
    setActiveSection(id);
    const targetElement = document.getElementById(id);
    if (targetElement) {
      const offset = 90;
      const elementPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  const handleInputChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.city) {
      alert('Please fill in all required fields.');
      return;
    }
    setFormSubmitted(true);
    setTimeout(() => {
      setForm({ name: '', phone: '', countryCode: '+91', email: '', city: '', message: '' });
      setFormSubmitted(false);
      alert('Thank you for contacting us! We will get back to you shortly.');
    }, 1500);
  };
  useEffect(() => {
    const TOP_OFFSET = 118;
    let sidebarHeight = 0;
    const checkSidebarState = () => {
      const container = mainLayoutRef.current;
      if (!container) return;
      if (!sidebarHeight) {
        sidebarHeight = container.querySelector('.sidebar-inner')?.offsetHeight || 0;
      }
      const rect = container.getBoundingClientRect();
      // Lock the fixed sidebar's X to its reserved grid column's real position.
      // The .grow-sidebar-nav column keeps its layout position/width no matter
      // what sidebarState is, so this is always the correct, stable offset —
      // unlike a vw-based calc, it isn't thrown off by scrollbar width changes.
      const navEl = sidebarNavRef.current;
      if (navEl) {
        const navRect = navEl.getBoundingClientRect();
        container.style.setProperty('--sidebar-fixed-left', `${navRect.left}px`);
      }
      let nextState;
      if (rect.top > TOP_OFFSET) {
        nextState = 'top';
      } else if (rect.bottom < TOP_OFFSET + sidebarHeight) {
        nextState = 'docked';
      } else {
        nextState = 'fixed';
      }
      setSidebarState((prev) => (prev === nextState ? prev : nextState));
    };
    checkSidebarState();
    window.addEventListener('scroll', checkSidebarState, { passive: true });
    window.addEventListener('resize', checkSidebarState, { passive: true });
    return () => {
      window.removeEventListener('scroll', checkSidebarState);
      window.removeEventListener('resize', checkSidebarState);
    };
  }, []);
  return (
    <div className="grow-page-wrapper">
      <Navbar />
      {/* SECTION 1: HERO (Full-width, top-level) */}
      <section className="grow-hero-section" id="hero-top">
        {/*
          NOTE: the extra "grow-desktopplus-*" classes below are ADDITIVE.
          They don't replace the original classes (hero-bg-image-wrapper,
          grow-hero-full-bg, hero-content-relative, hero-copy) so every
          existing breakpoint (0–700px, 700–750px, 750–1000px, and the
          base/global desktop rules) keeps working exactly as before.
          They only take effect inside the new `@media (min-width: 1001px)`
          block in GrowPage.css, where they fix the hero image position.
        */}
        <div className="hero-bg-image-wrapper grow-desktopplus-hero-image-wrapper">
          <img
            src="https://static.wixstatic.com/media/73d0ef_db687392d25e47e68044e2abc6af007e~mv2.jpg/v1/fill/w_1901,h_594,fp_0.45_0.63,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Untitled%20design_edited.jpg"
            alt="Grow with Haatza Representative"
            className="grow-hero-full-bg grow-desktopplus-hero-bg"
          />
        </div>
        <div className="container hero-content-relative grow-desktopplus-hero-content">
          <div className="hero-copy grow-desktopplus-hero-copy">
            <h1 className="hero-heading">
              Grow Your Business with <span className="highlight-blue">Haatza</span>
            </h1>
            <p className="hero-subtext">
              Sell your products online and reach <span className="highlight-blue">crores of customers</span> through Haatza's selling tools for suppliers
            </p>
            <div className="hero-callout-simple grow-gst-unique-box">
              <span className="new-badge-pill grow-gst-unique-badge">NEW</span>
              <div className="gst-callout-text mob700-gst-tight grow-gst-unique-text-wrap">
                <div className="gst-callout-line mob700-gst-line grow-gst-unique-line1">Don't have a GSTIN or have a Composition GSTIN?</div>
                <div className="gst-callout-line mob700-gst-line grow-gst-unique-line2">You can still sell on Haatza. Click <a href="/donothavegst" className="highlight-blue underline font-semibold grow-gst-unique-link">here</a> to know more.</div>
              </div>
            </div>
            <a href="/signin" style={{ textDecoration: 'none' }}>
              <button className="hero-cta-btn">Start Selling</button>
            </a>
          </div>
        </div>
      </section>
      {/* SECTION 2: DELIVER PRODUCTS (Full-width, top-level) */}
      <section className="deliver-products-section" id="deliver-products">
        <div className="container">
          <div className="deliver-content-left">
            <h2 className="grow-sub-section-title">Deliver products across India</h2>
            <p className="deliver-subtitle">
              Haatza ensures quick and hassle-free delivery of all your products <br />
              across India
            </p>
          </div>
          <div className="stat-cards-grid">
            <div className="stat-card mob700-stat-card">
              <div className="stat-icon-wrapper">
                <img src="https://static.wixstatic.com/media/fe689e_29e581a64ccb4decaeb0415ae3d6c9f3~mv2.png/v1/fill/w_74,h_57,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%207_edited.png" alt="Customers" className="stat-icon-img" />
              </div>
              <span className="deliver-stat-card-title">10 crore+ Haatza customers</span>
            </div>
            <div className="stat-card mob700-stat-card">
              <div className="stat-icon-wrapper">
                <img src="https://static.wixstatic.com/media/fe689e_40e20dbf7f7f4bff8c992a94923d3fab~mv2.png/v1/fill/w_74,h_74,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%209_edited.png" alt="Payments" className="stat-icon-img" />
              </div>
              <span className="deliver-stat-card-title">7* days secure & regular payments</span>
            </div>
            <div className="stat-card mob700-stat-card">
              <div className="stat-icon-wrapper">
                <img src="https://static.wixstatic.com/media/fe689e_f88547cc5cf44cca946ebb954edd5091~mv2.png/v1/fill/w_68,h_74,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%209_edited.png" alt="Low cost" className="stat-icon-img" />
              </div>
              <span className="deliver-stat-card-title">Low cost of doing business</span>
            </div>
          </div>
        </div>
      </section>
      {/* Main Container Layout for sections 3 to 11 */}
      <div className="grow-main-layout" ref={mainLayoutRef}>
        {/* Left Sticky Sidebar Sub-Nav */}
        <aside
          ref={sidebarNavRef}
          className={`grow-sidebar-nav ${sidebarState === 'fixed' ? 'grow-sidebar-nav--fixed' : ''} ${sidebarState === 'docked' ? 'grow-sidebar-nav--docked' : ''}`}
        >
          <div className="sidebar-inner">
            <ul className="sidebar-links">
              {sidebarItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => handleSidebarClick(item.id)}
                    className={`sidebar-link-btn ${activeSection === item.id ? 'active' : ''}`}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>
        {/* Right Scrollable Content */}
        <main className="grow-content-container">
          {/* SECTION 3: ADVERTISE YOUR PRODUCTS */}
          <section
            className="advertise-products-card shadow-sm border section-anchor"
            id="advertise-products"
            ref={(el) => (sectionsRef.current['advertise-products'] = el)}
          >
            <h2 className="grow-sub-section-title">Advertise Your Products</h2>
            <p className="section-intro-text">
              Promote your products and catalogs by creating sponsored advertisements or Haatza Ads. This will help you to reach the right customers and increase sales. Your catalogs will list on top slots on discovery pages across Haatza. It looks like a regular catalog listing with an 'ad' tag. Currently, not available for sellers who don't have a Regular GSTIN.
            </p>
            <div className="features-grid-3x2">
              {/* Feature 1 */}
              <div className="feature-item-card">
                <div className="feature-card-header">
                  <img src="https://static.wixstatic.com/media/fe689e_4e2395032f5a49c8bc491e29e53b8fb1~mv2.jpg/v1/fill/w_64,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-23%20at%209_52_edited.jpg" alt="" className="feature-icon-img" />
                  <span className="feature-item-card-title mob700-feature-title">Boost Visibility</span>
                </div>
                <p className="feature-desc">
                  Advertising on Haatza boosts the rank of your catalogs. It means higher visibility, leads to more clicks and increases orders.
                </p>
              </div>
              {/* Feature 2 */}
              <div className="feature-item-card">
                <div className="feature-card-header">
                  <img src="https://static.wixstatic.com/media/fe689e_01b0ea1eecf54d24bb504970ed250a59~mv2.jpg/v1/fill/w_66,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-23%20at%209_53_edited.jpg" alt="" className="feature-icon-img" />
                  <span className="feature-item-card-title mob700-feature-title">Increase Your Sales</span>
                </div>
                <p className="feature-desc">
                  By being present at the right place at the right time, your catalogs get more orders. Reaching the right customers helps you increase your sales.
                </p>
              </div>
              {/* Feature 3 */}
              <div className="feature-item-card">
                <div className="feature-card-header">
                  <img src="https://static.wixstatic.com/media/fe689e_fdf61b1e66bb434f92fd3e50692c5082~mv2.jpg/v1/fill/w_56,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-23%20at%209_54_edited.jpg" alt="" className="feature-icon-img" />
                  <span className="feature-item-card-title mob700-feature-title">Attract More Customers</span>
                </div>
                <p className="feature-desc">
                  Interested customers can view and shop your product catalogs from the top slots.
                </p>
              </div>
              {/* Feature 4 */}
              <div className="feature-item-card">
                <div className="feature-card-header">
                  <img src="https://static.wixstatic.com/media/fe689e_ef06099a64aa48fba9091713b67ecfed~mv2.jpg/v1/fill/w_65,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-23%20at%209_54_edited.jpg" alt="" className="feature-icon-img" />
                  <span className="feature-item-card-title mob700-feature-title">Track & Manage Ads</span>
                </div>
                <p className="feature-desc">
                  Make better decisions with performance metrics like Return on Investment and Budget Utilized.
                </p>
              </div>
              {/* Feature 5 */}
              <div className="feature-item-card">
                <div className="feature-card-header">
                  <img src="https://static.wixstatic.com/media/fe689e_8760f38f7d1043bd86f1f35c5cd45193~mv2.jpg/v1/fill/w_64,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-23%20at%209_55_edited.jpg" alt="" className="feature-icon-img" />
                  <span className="feature-item-card-title mob700-feature-title">Pay only for Click</span>
                </div>
                <p className="feature-desc">
                  No cost to pay unless a customer clicks on your advertisement.
                </p>
              </div>
              {/* Feature 6 */}
              <div className="feature-item-card">
                <div className="feature-card-header">
                  <img src="https://static.wixstatic.com/media/fe689e_0b6fe9b00a6b433985ad71c1c95004e1~mv2.jpg/v1/fill/w_62,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-23%20at%209_57_edited.jpg" alt="" className="feature-icon-img" />
                  <span className="feature-item-card-title mob700-feature-title">Control Costs</span>
                </div>
                <p className="feature-desc">
                  You create the campaigns, you decide when to run or pause them through the performance data.
                </p>
              </div>
            </div>
          </section>
          {/* SECTION 4: HOW CAMPAIGN WORKS */}
          <section
            className="how-campaign-card shadow-sm border section-anchor"
            id="how-campaign-works"
            ref={(el) => (sectionsRef.current['how-campaign-works'] = el)}
          >
            <h2 className="grow-sub-section-title">Start Your Ad Campaign in 3 Simple Steps</h2>
            <div className="steps-grid-3">
              <div className="step-item-card">
                <div className="step-header-row">
                  <div className="step-icon-badge"><img src="https://static.wixstatic.com/media/fe689e_ef06099a64aa48fba9091713b67ecfed~mv2.jpg/v1/fill/w_65,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-23%20at%209_54_edited.jpg" alt="" className="step-icon-img" /></div>
                  <span className="step-item-card-title mob700-campaign-step-title">Choose Catalog</span>
                </div>
                <p className="step-desc">Select the product catalog or catalogs you want to advertise</p>
              </div>
              <div className="step-item-card">
                <div className="step-header-row">
                  <div className="step-icon-badge"><img src="https://static.wixstatic.com/media/fe689e_0b6fe9b00a6b433985ad71c1c95004e1~mv2.jpg/v1/fill/w_62,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-23%20at%209_57_edited.jpg" alt="" className="step-icon-img" /></div>
                  <span className="step-item-card-title mob700-campaign-step-title">Select Budget</span>
                </div>
                <p className="step-desc">Allocate an advertising budget for your campaign</p>
              </div>
              <div className="step-item-card">
                <div className="step-header-row">
                  <div className="step-icon-badge"><img src="https://static.wixstatic.com/media/fe689e_6ce5d46c27684429aacbdb42eab1e2ae~mv2.jpg/v1/fill/w_63,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-22%20at%209_43_edited.jpg" alt="" className="step-icon-img" /></div>
                  <span className="step-item-card-title mob700-campaign-step-title">Select Duration</span>
                </div>
                <p className="step-desc">Choose how long you want to run the advertisement campaign</p>
              </div>
            </div>
            <div className="learn-more-callout">
              Learn More About Advertisement on Haatza by Visiting Our{' '}
              <a href="https://www.haatzaseller.com/pricing-plans/list" target="_blank" rel="noopener noreferrer" className="inline-link">
                Haatza Ads Page
              </a>
            </div>
            <div className="youtube-player-wrapper">
              <iframe
                src="https://www.youtube.com/embed/fRfJmeYmTH0"
                title="How to Promote Your Products | Haatza Seller"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="embed-iframe"
              ></iframe>
            </div>
          </section>
          {/* SECTION 5: PRODUCT RECOMMENDATION */}
          <section
            className="product-rec-card shadow-sm border section-anchor"
            id="product-recommendation"
            ref={(el) => (sectionsRef.current['product-recommendation'] = el)}
          >
            <h2 className="grow-sub-section-title">Increase Sales Through Product Recommendation</h2>
            <p className="section-intro-text">
              With Haatza Supplier Panel, you get access to the Product Recommendation tool that is very easy to use and helps in expanding your business as well.
            </p>
            <h3 className="section-h3">Recommendations are made in a few ways:</h3>
            <div className="outline-cards-grid">
              <div className="outline-card">
                <h4 className="outline-card-title text-gradient mob700-outline-title">Trending searches</h4>
                <p className="outline-card-text">Products people are looking for but can't find anything they like.</p>
              </div>
              <div className="outline-card">
                <h4 className="outline-card-title text-gradient mob700-outline-title">Top selling</h4>
                <p className="outline-card-text">Selling well on Haatza.</p>
              </div>
              <div className="outline-card">
                <h4 className="outline-card-title text-gradient mob700-outline-title">Other categories</h4>
                <p className="outline-card-text">Suggestions based on categories similar to what you are selling.</p>
              </div>
            </div>
          </section>
          {/* SECTION 5-2: RECOMMENDATIONS STEPS */}
          <section
            className="recommendations-steps-card shadow-sm border section-anchor"
            id="recommendations-ways"
            ref={(el) => (sectionsRef.current['recommendations-ways'] = el)}
          >
            <h3 className="section-h3 mob700-rec-heading">Increase sales through product recommendations in 3 simple steps</h3>
            <div className="steps-grid-3">
              <div className="step-item-card">
                <div className="step-header-row">
                  <span className="step-item-card-title mob700-rec-step-title">Explore Haatza recommendations</span>
                </div>
                <p className="step-desc">We curate product recommendations for you based on customer feedback and data</p>
              </div>
              <div className="step-item-card">
                <div className="step-header-row">
                  <span className="step-item-card-title mob700-rec-step-title">Accept source & Recommendations</span>
                </div>
                <p className="step-desc">Accept the recommendations provided by Haatza and source the products that customers want</p>
              </div>
              <div className="step-item-card">
                <div className="step-header-row">
                  <span className="step-item-card-title mob700-rec-step-title">Upload Recommendations</span>
                </div>
                <p className="step-desc">Add & upload the product recommended to your catalog and boost your sales</p>
              </div>
            </div>
          </section>
          {/* SECTION 6: INCREASE SALES */}
          <section
            className="increase-sales-card shadow-sm border section-anchor"
            id="increase-sales"
            ref={(el) => (sectionsRef.current['increase-sales'] = el)}
          >
            <div className="learn-more-callout">
              Learn More About Advertisement on Haatza by Visiting Our{' '}
              <a href="https://www.haatzaseller.com/pricing-plans/list" target="_blank" rel="noopener noreferrer" className="inline-link">
                Haatza Ads Page
              </a>
            </div>
            <div className="youtube-player-wrapper">
              <iframe
                src="https://www.youtube.com/embed/fRfJmeYmTH0"
                title="How to Promote Your Products | Haatza Seller"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="embed-iframe"
              ></iframe>
            </div>
          </section>
          {/* SECTION 7: PRICE RECOMMENDATION */}
          <section
            className="price-rec-card shadow-sm border section-anchor"
            id="price-recommendation"
            ref={(el) => (sectionsRef.current['price-recommendation'] = el)}
          >
            <h2 className="grow-sub-section-title">Attract More Customers Through Price Recommendation</h2>
            <p className="section-intro-text">
              You always wanted to be a forerunner of your store and keep your prices competitive but you had no tool to have the most profitable prices when selling online. Now, with Haatza's Price Recommendation tool you can easily set up your prices across Haatza and always stay ahead of competitors.
            </p>
            <div className="learn-more-callout">
              Learn More About Haatza Price Recommendation {' '}
              <a href="https://youtu.be/lBrLd0mHpss?si=kLQLli5LRdgvvbiI" target="_blank" rel="noopener noreferrer" className="inline-link">
              </a>
            </div>
            <div className="youtube-player-wrapper">
              <iframe
                src="https://www.youtube.com/embed/lBrLd0mHpss"
                title="How Price Recommendation Works on Haatza"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="embed-iframe"
              ></iframe>
            </div>
          </section>
          {/* SECTION 8: QUALITY DASHBOARD */}
          <section
            className="quality-dashboard-card shadow-sm border section-anchor"
            id="quality-dashboard"
            ref={(el) => (sectionsRef.current['quality-dashboard'] = el)}
          >
            <h2 className="grow-sub-section-title">Reduce Returns Through Quality Dashboard</h2>
            <p className="section-intro-text">
              When your catalogs become live on Haatza, they will be visible to the crores of customers who shop on Haatza. Every time a customer uses the Haatza platform and provides feedback, it will immediately reflect on your Quality dashboard. This will help you understand how customers are responding to your products and thereby optimise your inventory in the future.
            </p>
            <div className="learn-more-callout">
              Learn More About Quality Dashboard{' '}
              <a href="https://youtu.be/Wh-nXR7rxjs?si=rEzwOZC9HmQtMtF3" target="_blank" rel="noopener noreferrer" className="inline-link">
              </a>
            </div>
            <div className="youtube-player-wrapper">
              <iframe
                src="https://www.youtube.com/embed/Wh-nXR7rxjs"
                title="How Quality Insights Help You Sell Better on Haatza"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="embed-iframe"
              ></iframe>
            </div>
          </section>
          {/* SECTION 9: NEXT DAY DISPATCH */}
          <section
            className="ndd-program-card shadow-sm border section-anchor"
            id="ndd-program"
            ref={(el) => (sectionsRef.current['ndd-program'] = el)}
          >
            <h2 className="grow-sub-section-title">Next Day Dispatch Program</h2>
            <p className="section-intro-text">
              To increase visibility and improve customer satisfaction, suppliers can sign up for the Next Day Dispatch program. The NDD program requires those registered to dispatch their orders the next day. Sellers receive a special badge that tells customers they can receive their items in one day after ordering. As a result, customers are more likely to order from you.
            </p>
            <div className="learn-more-callout">
              Learn More About Next Day Dispatch Program {' '}
              <a href="https://youtu.be/wRJBS49wzz0?si=kiZNmlWEZSy1Emec" target="_blank" rel="noopener noreferrer" className="inline-link">
              </a>
            </div>
            <div className="youtube-player-wrapper">
              <iframe
                src="https://www.youtube.com/embed/wRJBS49wzz0"
                title="How to Do Next Day Dispatch on Haatza"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="embed-iframe"
              ></iframe>
            </div>
          </section>
          {/* SECTION 10: BENEFITS OF NDD */}
          <section
            className="ndd-benefits-card shadow-sm border section-anchor"
            id="ndd-benefits"
            ref={(el) => (sectionsRef.current['ndd-benefits'] = el)}
          >
            <h2 className="grow-sub-section-title">Benefits of Registering for NDD Program</h2>
            <div className="benefits-list-wrapper">
              <ul className="benefits-bullet-list">
                <li>
                  <span className="bullet-check">•</span>
                  <div className="bullet-info">
                    <strong>Higher visibility</strong>
                  </div>
                </li>
                <li>
                  <span className="bullet-check">•</span>
                  <div className="bullet-info">
                    <strong>Lesser order cancellation</strong>
                  </div>
                </li>
                <li>
                  <span className="bullet-check">•</span>
                  <div className="bullet-info">
                    <strong>Catalogs appear at the top of the homepage</strong>
                  </div>
                </li>
                <li>
                  <span className="bullet-check">•</span>
                  <div className="bullet-info">
                    <strong>Lesser chances of RTO</strong>
                  </div>
                </li>
                <li>
                  <span className="bullet-check">•</span>
                  <div className="bullet-info">
                    <strong>Get your own PE Account Manager</strong>
                  </div>
                </li>
              </ul>
            </div>
          </section>
          {/* SECTION 11: STEPS TO REGISTER FOR NDD */}
          <section
            className="ndd-steps-card shadow-sm border section-anchor"
            id="ndd-steps"
            ref={(el) => (sectionsRef.current['ndd-steps'] = el)}
          >
            <h2 className="grow-sub-section-title">Steps to Register for Haatza NDD Program</h2>
            <div className="numbered-steps-grid">
              <div className="num-step-card">
                <span className="step-num-badge">1</span>
                <p className="step-text">Login to your "Seller App"</p>
              </div>
              <div className="num-step-card">
                <span className="step-num-badge">2</span>
                <p className="step-text">Click on "Help"</p>
              </div>
              <div className="num-step-card">
                <span className="step-num-badge">3</span>
                <p className="step-text">Select "Orders & Delivery"</p>
              </div>
              <div className="num-step-card">
                <span className="step-num-badge">4</span>
                <p className="step-text">
                  Choose "I want to add my catalogs under Next Day Dispatch Program/I want to put my complete account under Next Day Dispatch (NDD) program"
                </p>
              </div>
              <div className="num-step-card">
                <span className="step-num-badge">5</span>
                <p className="step-text">Raise Ticket</p>
              </div>
            </div>
          </section>
        </main>
      </div>
      {/* SECTION 12: BOTTOM CTA & LEAD FORM (Full-width, top-level) */}
      <section className="grow-lead-form-section" id="lead-form">
        <div className="container">
          <div className="lead-grid-2">
            {/* Left Column Text */}
            <div className="lead-copy">
              <h2 className="lead-title">
                Sell Products Online at 0% <br /> Commission on Haatza
              </h2>
              <p className="lead-text">
                Join the fastest growing e-commerce platform in India and <br /> sell to crores of users and grow your business.
              </p>
              <a href="/signin" style={{ textDecoration: 'none' }}>
                <button className="lead-cta-btn">Start Selling</button>
              </a>
            </div>
            {/* Right Column Form */}
            <div className="lead-form-wrapper">
              <h3 className="form-wrapper-title">Keep in touch</h3>
              <form onSubmit={handleFormSubmit} className="lead-form-element">
                <div className="form-field">
                  <label>Name*</label>
                  <input
                    type="text"
                    required
                    placeholder="Enter your first name"
                    value={form.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>Phone*</label>
                  <InternationalPhoneInput
                    phone={form.phone}
                    onChangePhone={(val) => setForm(prev => ({ ...prev, phone: val }))}
                    countryCode={form.countryCode}
                    onChangeCountryCode={(code) => setForm(prev => ({ ...prev, countryCode: code }))}
                    required
                  />
                </div>
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    placeholder="Enter email address"
                    value={form.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>City*</label>
                  <input
                    type="text"
                    required
                    placeholder="Enter your city"
                    value={form.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>Message</label>
                  <textarea
                    rows="3"
                    placeholder="Write your query here..."
                    value={form.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                  ></textarea>
                </div>
                <button type="submit" className="form-submit-btn" disabled={formSubmitted}>
                  {formSubmitted ? 'Submitting...' : 'Submit'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}